import SignalWifiStatusbarConnectedNoInternet4Icon from "@mui/icons-material/SignalWifiStatusbarConnectedNoInternet4";
import { Alert, Slide } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { AppColors } from "../../util/AppColors";
import { baseURL } from "../../util/server";

const SLOW_REQUEST_MS = 8000;
const AUTO_HIDE_MS = 1000;
const SLOW_EFFECTIVE_TYPES = ["slow-2g", "2g"];

const getConnection = () =>
  navigator.connection ||
  navigator.mozConnection ||
  navigator.webkitConnection ||
  null;

const isConnectionSlow = (connection) => {
  if (!connection) return false;
  if (SLOW_EFFECTIVE_TYPES.includes(connection.effectiveType)) return true;
  if (typeof connection.rtt === "number" && connection.rtt >= 500) return true;
  if (
    typeof connection.downlink === "number" &&
    connection.downlink > 0 &&
    connection.downlink <= 0.5
  ) {
    return true;
  }
  return false;
};

/**
 * Top banner for offline / slow network.
 * Shows once per episode, auto-hides after 2s — no close button.
 */
const NetworkIndicator = () => {
  const [status, setStatus] = useState(null); // "offline" | "slow" | null
  const [visible, setVisible] = useState(false);
  const notifiedEpisodeRef = useRef(null); // last notified status
  const slowFromRequestRef = useRef(false);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    const connection = getConnection();

    const showBanner = (next) => {
      // Only notify once per continuous offline/slow episode
      if (notifiedEpisodeRef.current === next) return;
      notifiedEpisodeRef.current = next;
      setStatus(next);
      setVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
      }, AUTO_HIDE_MS);
    };

    const clearEpisode = () => {
      notifiedEpisodeRef.current = null;
      setStatus(null);
      setVisible(false);
    };

    const evaluate = () => {
      if (typeof navigator.onLine === "boolean" && !navigator.onLine) {
        slowFromRequestRef.current = false;
        showBanner("offline");
        return;
      }
      if (isConnectionSlow(getConnection()) || slowFromRequestRef.current) {
        showBanner("slow");
        return;
      }
      clearEpisode();
    };

    const handleOffline = () => {
      slowFromRequestRef.current = false;
      showBanner("offline");
    };
    const handleOnline = () => evaluate();
    const handleConnectionChange = () => {
      if (!isConnectionSlow(getConnection())) {
        slowFromRequestRef.current = false;
      }
      evaluate();
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    connection?.addEventListener?.("change", handleConnectionChange);

    // Fallback when Network Information API is missing (e.g. Safari):
    // observe slow API responses — does not alter requests.
    let observer;
    let clearSlowTimer;
    if (typeof PerformanceObserver !== "undefined") {
      try {
        observer = new PerformanceObserver((list) => {
          if (typeof navigator.onLine === "boolean" && !navigator.onLine)
            return;
          const slowHit = list.getEntries().some((entry) => {
            const name = entry.name || "";
            if (!name.startsWith(baseURL)) return false;
            return (entry.duration || 0) >= SLOW_REQUEST_MS;
          });
          if (slowHit) {
            slowFromRequestRef.current = true;
            showBanner("slow");
            if (clearSlowTimer) clearTimeout(clearSlowTimer);
            clearSlowTimer = setTimeout(() => {
              slowFromRequestRef.current = false;
              evaluate();
            }, 30000);
          }
        });
        observer.observe({ entryTypes: ["resource"] });
      } catch {
        // ignore unsupported observer
      }
    }

    evaluate();

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      connection?.removeEventListener?.("change", handleConnectionChange);
      observer?.disconnect?.();
      if (clearSlowTimer) clearTimeout(clearSlowTimer);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const isOffline = status === "offline";

  return (
    <Slide direction="down" in={visible} mountOnEnter unmountOnExit>
      <Alert
        severity={isOffline ? "error" : "warning"}
        icon={
          <SignalWifiStatusbarConnectedNoInternet4Icon fontSize="inherit" />
        }
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9990,
          borderRadius: 0,
          justifyContent: "center",
          bgcolor: isOffline ? AppColors.snackError : AppColors.snackWarn,
          color: AppColors.white,
          "& .MuiAlert-icon, & .MuiAlert-message": {
            color: AppColors.white,
          },
        }}
      >
        {isOffline
          ? "You are offline. Check your internet connection."
          : "Slow network detected. Some actions may take longer than usual."}
      </Alert>
    </Slide>
  );
};

export default NetworkIndicator;
